import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { useAnnee } from '../hooks/useAnnee';
import { api } from '../lib/apiClient';
import { Chargement, EnTetePage, Erreur, Infobulle } from '../components/Elements';
import { DashboardActivite } from './DashboardActivite';
import { formaterMois, formaterMontant } from '../lib/format';
import type { Dashboard, TableauBord, DimensionVentilation, LigneVentilation } from '../types';

const DIMENSIONS: { cle: DimensionVentilation; libelle: string }[] = [
  { cle: 'type', libelle: 'type' },
  { cle: 'lieu', libelle: 'lieu' },
  { cle: 'age', libelle: 'âge' },
  { cle: 'professeur', libelle: 'professeur' },
  { cle: 'arrondissement', libelle: 'arrondissement' },
];

/**
 * Tableau de bord (CDC M7).
 *
 * Reproduit celui du classeur, dont les chiffres font foi : le
 * récapitulatif par activité ne compte que les inscriptions ACTIVES, les
 * arrêts en cours d'année et les adhésions rejoignant la dette totale.
 * S'en écarter, c'est afficher des nombres qui ne se rapprochent plus.
 */
export function TableauDeBordPage() {
  const { entiteActive, session, peut } = useAuth();
  const cle = entiteActive?.id ?? 'aucune';
  // `null` = vue cumulee. Par defaut, l'entite de la session : c'est
  // celle sur laquelle on travaille.
  const [vue, setVue] = useState<string | null>(cle);
  const [vueInitialisee, setVueInitialisee] = useState(cle);

  // Basculer d'entite dans le menu utilisateur doit ramener le tableau
  // sur la nouvelle entite, pas laisser l'ancienne affichee.
  if (vueInitialisee !== cle) {
    setVueInitialisee(cle);
    setVue(cle);
  }

  const annee = useAnnee();
  const peutCumuler = peut('tableau_bord.agrege');
  const entites = session?.entites ?? [];

  // Le dashboard FINANCIER se pilote par habilitation, sur l'entite
  // REGARDEE. Le cumule suit les droits de l'entite de session.
  const financesAccessibles = entites.find((e) => e.id === (vue ?? cle))?.accesFinances ?? false;
  const [dashboard, setDashboard] = useState<Dashboard>(
    session?.utilisateur.dashboardDefaut ?? 'activite',
  );
  // Le defaut du compte peut viser un dashboard inaccessible ici : on
  // retombe sur Activite, le socle, plutot que d'afficher un refus.
  const dashboardEffectif: Dashboard =
    dashboard === 'finances' && !financesAccessibles ? 'activite' : dashboard;

  const bord = useRessource(
    () =>
      annee.anneeId && dashboardEffectif === 'finances'
        ? api.get<TableauBord>('/api/tableau-bord', {
            anneeId: annee.anneeId,
            cumule: vue === null ? 'true' : undefined,
            entiteId: vue && vue !== cle ? vue : undefined,
          })
        : Promise.resolve(null),
    [cle, annee.anneeId, vue, dashboardEffectif],
  );

  if (annee.chargement) return <Chargement />;

  const donnees = bord.donnees;
  const dette = donnees?.dette;
  const taux = dette && dette.total > 0 ? (dette.ventile / dette.total) * 100 : 0;

  return (
    <>
      <EnTetePage
        titre={`Bonjour ${session?.utilisateur.prenom ?? ''}`}
        description={
          entiteActive
            ? `Vous travaillez sur ${entiteActive.raisonSociale}, avec le role ${entiteActive.roleLibelle}.`
            : "Aucune entite active : demandez une habilitation a l'administrateur."
        }
      />

      <div className="carte mb-4 flex flex-wrap items-center gap-4 px-4 py-3">
        {/* La bascule de dashboard reprend le bloc segmente des entites.
            Elle n'apparait que si le financier est accessible ici : un
            seul dashboard, pas de faux controle. */}
        {financesAccessibles && (
          <div className="flex overflow-hidden rounded-md border border-encre-200 text-sm">
            <button
              type="button"
              className={`px-3 py-1 ${
                dashboardEffectif === 'activite' ? 'bg-encre-100 font-medium' : 'text-encre-500'
              }`}
              onClick={() => setDashboard('activite')}
            >
              Activité
            </button>
            <button
              type="button"
              className={`border-l border-encre-200 px-3 py-1 ${
                dashboardEffectif === 'finances' ? 'bg-encre-100 font-medium' : 'text-encre-500'
              }`}
              onClick={() => setDashboard('finances')}
            >
              Finances
            </button>
          </div>
        )}

        {/* RG-33 : le cumulé n'est pas une entité de plus, c'est une autre
            lecture. L'entité affichée par défaut reste celle de la
            session ; on change d'entité par le menu utilisateur. */}
        {/* Toutes les entites sur lesquelles on est habilite, plus le
            cumule (RG-33). On LIT ici une autre ecole sans basculer la
            session : le tableau de bord se consulte des deux cotes, le
            travail se fait sur une seule. */}
        {(entites.length > 1 || peutCumuler) && (
          <div className="flex overflow-hidden rounded-md border border-encre-200 text-sm">
            {entites.map((entite) => (
              <Infobulle
                key={entite.id}
                texte={entite.raisonSociale}
                className="flex border-l border-encre-200 first:border-l-0"
              >
                <button
                  type="button"
                  className={`px-3 py-1 ${vue === entite.id ? 'bg-encre-100 font-medium' : 'text-encre-500'}`}
                  onClick={() => setVue(entite.id)}
                >
                  {entite.code}
                </button>
              </Infobulle>
            ))}
            {peutCumuler && entites.length > 1 && (
              <button
                type="button"
                className={`border-l border-encre-200 px-3 py-1 ${
                  vue === null ? 'bg-encre-100 font-medium' : 'text-encre-500'
                }`}
                onClick={() => setVue(null)}
              >
                Cumulé
              </button>
            )}
          </div>
        )}

        {vue !== null && vue !== cle && (
          <span className="text-xs text-encre-500">
            Lecture seule — vous travaillez sur {entiteActive?.code}.
          </span>
        )}

        {donnees?.cumule && (
          <span className="text-xs text-amber-700">
            Vue cumulée — {donnees.entites.map((e) => e.code).join(' + ')}. Sans valeur comptable
            pour une entité prise seule.
          </span>
        )}
      </div>

      {dashboardEffectif === 'activite' && annee.anneeId && (
        <DashboardActivite cle={cle} vue={vue} anneeId={annee.anneeId} />
      )}

      {dashboardEffectif === 'finances' && bord.erreur && (
        <Erreur message={bord.erreur} onReessayer={bord.recharger} />
      )}
      {dashboardEffectif === 'finances' && bord.chargement && <Chargement />}

      {dashboardEffectif === 'finances' && donnees && dette && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tuile
              libelle="Chiffre d’affaire"
              valeur={formaterMontant(dette.total)}
              detail={`${donnees.effectifs.inscriptions_actives} actives · ${donnees.effectifs.arrets} arrêts · ${donnees.effectifs.adhesions} adhésions`}
            />
            <Tuile
              libelle="Encaissé"
              valeur={formaterMontant(dette.ventile)}
              detail={`${taux.toFixed(1).replace('.', ',')} % du dû`}
              ton="succes"
            />
            <Tuile
              libelle="Reste à encaisser"
              valeur={formaterMontant(dette.reste)}
              detail={
                donnees.impayes.length === 0
                  ? 'Tout est réglé'
                  : `${donnees.impayes.length} adhérent(s)`
              }
              ton={dette.reste > 0 ? 'alerte' : 'neutre'}
            />
            <Tuile
              libelle="Membres usagers"
              valeur={String(donnees.effectifs.usagers)}
              detail={`dont ${donnees.effectifs.membres_actifs} membres actifs`}
            />
          </div>

          {/* La decomposition du calcul a ete retiree de l'ecran. Le
              signalement des encaissements qui ne couvrent aucune dette
              reste : sans lui, l'identite comptable serait fausse en
              silence. Il n'apparait que s'il y a quelque chose a dire. */}
          {dette.non_ventile > 0 && (
            <p className="carte mt-4 px-4 py-2.5 text-sm text-amber-700">
              {formaterMontant(dette.non_ventile)} reçus sans dette à couvrir
            </p>
          )}

          <CourbeEncaissement points={donnees.encaissement} cible={dette.total} />

          <div className="mt-4 grid gap-4 lg:grid-cols-[1.2fr_1fr] lg:items-start">
            <TableauActivites lignes={donnees.activites} />
            <div className="space-y-4">
              <BlocVentilations ventilations={donnees.ventilations} />
              <BlocATraiter donnees={donnees} />
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Tuile({
  libelle,
  valeur,
  detail,
  ton = 'neutre',
}: {
  libelle: string;
  valeur: string;
  detail: string;
  ton?: 'neutre' | 'succes' | 'alerte';
}) {
  const couleurs = {
    neutre: 'text-encre-900',
    succes: 'text-emerald-700',
    alerte: 'text-red-700',
  } as const;

  return (
    <div className={`carte h-full p-5 ${ton === 'alerte' ? 'border-red-200' : ''}`}>
      <div className="text-xs font-medium uppercase tracking-wide text-encre-400">{libelle}</div>
      <div className={`tabulaire mt-2 text-3xl font-semibold ${couleurs[ton]}`}>{valeur}</div>
      <div className="mt-1 text-xs text-encre-500">{detail}</div>
    </div>
  );
}

/**
 * Encaissement cumulé, comparé au dû de l'année.
 *
 * La courbe dit ce qu'un total ne dit pas : à quel rythme l'argent rentre.
 * Les chèques échelonnés étalent l'encaissement sur douze mois, et c'est
 * cette pente qu'on surveille en cours d'année.
 */
function CourbeEncaissement({
  points,
  cible,
}: {
  points: TableauBord['encaissement'];
  cible: number;
}) {
  if (points.length < 2) return null;

  const hauteur = 130;
  const largeur = 700;
  const marge = { gauche: 8, droite: 8, haut: 18, bas: 24 };
  const plafond = Math.max(cible, points[points.length - 1]!.cumul);

  const x = (index: number) =>
    marge.gauche +
    (index * (largeur - marge.gauche - marge.droite)) / Math.max(points.length - 1, 1);
  const y = (valeur: number) =>
    marge.haut + (1 - valeur / plafond) * (hauteur - marge.haut - marge.bas);

  const trace = points.map((point, index) => `${x(index)},${y(point.cumul)}`).join(' ');
  const jalons = points.filter((_, index) => index % 3 === 0);

  return (
    <div className="carte mt-4 p-4">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-encre-500">
          Encaissement cumulé
        </h2>
        <span className="text-xs text-encre-400">— réel · - - dû sur l’année</span>
      </div>
      <svg
        viewBox={`0 0 ${largeur} ${hauteur}`}
        className="w-full"
        role="img"
        aria-label={`Encaissement cumulé, de ${formaterMois(points[0]!.mois)} à ${formaterMois(points[points.length - 1]!.mois)}`}
      >
        <line
          x1={marge.gauche}
          y1={y(cible)}
          x2={largeur - marge.droite}
          y2={y(cible)}
          stroke="#b4b2a9"
          strokeDasharray="5 4"
        />
        <line
          x1={marge.gauche}
          y1={hauteur - marge.bas}
          x2={largeur - marge.droite}
          y2={hauteur - marge.bas}
          stroke="#d3d1c7"
        />
        <polyline fill="none" stroke="#059669" strokeWidth={2} points={trace} />
        {jalons.map((point) => (
          <text
            key={point.mois}
            x={x(points.indexOf(point))}
            y={hauteur - 8}
            fontSize={10}
            fill="#888780"
            textAnchor="middle"
          >
            {point.mois.slice(5)}/{point.mois.slice(2, 4)}
          </text>
        ))}
      </svg>
    </div>
  );
}

/** Récapitulatif par activité, du plus gros effectif au plus petit. */
function TableauActivites({ lignes }: { lignes: TableauBord['activites'] }) {
  const total = lignes.reduce(
    (cumul, ligne) => ({
      effectif: cumul.effectif + ligne.effectif,
      du: cumul.du + ligne.du,
      paye: cumul.paye + ligne.paye,
      reste: cumul.reste + ligne.reste,
    }),
    { effectif: 0, du: 0, paye: 0, reste: 0 },
  );

  return (
    <section className="carte">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-encre-200 px-4 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-encre-500">
          Par activité
        </h2>
        <span className="text-xs text-encre-400">
          inscriptions actives · {lignes.length} activités
        </span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-encre-400">
            <th className="px-4 py-2 text-left font-normal">Activité</th>
            <th className="px-2 py-2 text-right font-normal">Eff.</th>
            <th className="px-2 py-2 text-right font-normal">Dû</th>
            <th className="px-2 py-2 text-right font-normal">Payé</th>
            <th className="px-4 py-2 text-right font-normal">Reste</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-encre-100">
          {lignes.map((ligne) => (
            <tr key={ligne.libelle}>
              <td className="px-4 py-1.5">{ligne.libelle}</td>
              <td className="tabulaire px-2 py-1.5 text-right">{ligne.effectif}</td>
              <td className="tabulaire px-2 py-1.5 text-right">{formaterMontant(ligne.du)}</td>
              <td className="tabulaire px-2 py-1.5 text-right">{formaterMontant(ligne.paye)}</td>
              <td className="tabulaire px-4 py-1.5 text-right">
                {ligne.reste > 0 ? (
                  <span className="text-red-700">{formaterMontant(ligne.reste)}</span>
                ) : (
                  <span className="text-encre-300">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-encre-300 font-medium">
            <td className="px-4 py-2">Total</td>
            <td className="tabulaire px-2 py-2 text-right">{total.effectif}</td>
            <td className="tabulaire px-2 py-2 text-right">{formaterMontant(total.du)}</td>
            <td className="tabulaire px-2 py-2 text-right">{formaterMontant(total.paye)}</td>
            <td className="tabulaire px-4 py-2 text-right text-red-700">
              {total.reste > 0 ? formaterMontant(total.reste) : '—'}
            </td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

/**
 * Ventilations, en euros ou en nombre d'inscriptions.
 *
 * La bascule ne relance aucune requête : les deux mesures voyagent
 * ensemble. Elles ne disent pas la même chose — la chorale pèse 100
 * inscriptions pour 30 000 €, le piano 47 pour 44 000.
 */
function BlocVentilations({ ventilations }: { ventilations: TableauBord['ventilations'] }) {
  const [dimension, setDimension] = useState<DimensionVentilation>('type');
  const [enEuros, setEnEuros] = useState(true);

  const mesure = (ligne: LigneVentilation) => (enEuros ? ligne.montant : ligne.adherents);
  // Le classement suit la mesure affichee : Geoffrey encadre 53 eleves
  // pour 12 000 EUR, Marine 92 pour 25 000. Trier sur l'une en affichant
  // l'autre donne une liste qui a l'air melangee.
  const lignes = [...ventilations[dimension]].sort((a, b) => mesure(b) - mesure(a));
  const maximum = Math.max(...lignes.map(mesure), 1);

  return (
    <section className="carte p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-encre-500">
          Ventilations
        </h2>
        <div className="ml-auto flex overflow-hidden rounded-md border border-encre-200 text-xs">
          <button
            type="button"
            className={`px-2 py-1 ${enEuros ? 'bg-encre-100 font-medium' : 'text-encre-500'}`}
            onClick={() => setEnEuros(true)}
          >
            €
          </button>
          <button
            type="button"
            className={`border-l border-encre-200 px-2 py-1 ${
              !enEuros ? 'bg-encre-100 font-medium' : 'text-encre-500'
            }`}
            onClick={() => setEnEuros(false)}
          >
            adhérents
          </button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-3 text-sm">
        {DIMENSIONS.map((candidate) => (
          <button
            key={candidate.cle}
            type="button"
            className={
              candidate.cle === dimension
                ? 'border-b-2 border-encre-900 font-medium'
                : 'text-encre-500'
            }
            onClick={() => setDimension(candidate.cle)}
          >
            {candidate.libelle}
          </button>
        ))}
      </div>

      <ul className="space-y-1.5">
        {lignes.map((ligne) => (
          <li key={ligne.libelle} className="grid grid-cols-[8rem_1fr_5.5rem] items-center gap-2">
            <Infobulle texte={ligne.libelle} className="truncate text-sm">
              {ligne.libelle}
            </Infobulle>
            <span className="h-3 rounded-sm bg-encre-100">
              <span
                className="block h-3 rounded-sm bg-encre-500"
                style={{ width: `${Math.max((mesure(ligne) / maximum) * 100, 1)}%` }}
              />
            </span>
            <span className="tabulaire text-right text-sm">
              {enEuros ? formaterMontant(ligne.montant) : ligne.adherents}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Ce qui appelle une action, réuni en un seul endroit.
 *
 * Les impayés, les encaissements sans dette et les anomalies vivent sur
 * trois écrans différents : personne ne va les y chercher. Le tableau de
 * bord est le seul écran qu'on ouvre tous les jours.
 */
function BlocATraiter({ donnees }: { donnees: TableauBord }) {
  // Le compteur vient du payload, donc du meme scope que le reste : le
  // demander a part affichait les anomalies de l'entite en SESSION,
  // meme quand l'ecran regarde l'autre ecole.
  const totalAnomalies = donnees.anomalies;
  const rienASignaler =
    donnees.impayes.length === 0 && donnees.dette.non_ventile === 0 && totalAnomalies === 0;

  if (rienASignaler) {
    return (
      <section className="rounded-lg border border-dashed border-encre-200 px-4 py-3 text-sm text-encre-500">
        Rien à traiter : tout est réglé et aucune anomalie n’est signalée.
      </section>
    );
  }

  return (
    <section className="carte border-amber-200 bg-amber-50 p-4">
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-amber-900">
        À traiter
      </h2>
      <ul className="space-y-1.5 text-sm text-amber-900">
        {donnees.impayes.slice(0, 5).map((impaye) => (
          <li key={impaye.personne_id}>
            <Link to={`/adherents/${impaye.personne_id}`} className="underline">
              {impaye.prenom} <span className="uppercase">{impaye.nom}</span>
            </Link>{' '}
            <span className="tabulaire">— {formaterMontant(impaye.montant)} non réglés</span>
          </li>
        ))}
        {donnees.impayes.length > 5 && (
          <li className="text-xs">… et {donnees.impayes.length - 5} autre(s)</li>
        )}
        {donnees.dette.non_ventile > 0 && (
          <li>
            <Link to="/paiements" className="underline">
              {formaterMontant(donnees.dette.non_ventile)} encaissés sans dette à couvrir
            </Link>
          </li>
        )}
        {totalAnomalies > 0 && (
          <li>
            <Link to="/anomalies" className="underline">
              {totalAnomalies} anomalie(s) de données
            </Link>
          </li>
        )}
      </ul>
    </section>
  );
}
